/**
 * Kho dữ liệu v2 — MỘT listener Firestore cho mỗi collection, mọi màn hình đọc chung.
 * (Bản cũ: App.jsx giữ 6 listener, ~10 component tự subscribe thêm, và mỗi lần
 * dữ liệu đổi lại đọc lại ~9 truy vấn.) Chỉ ĐỌC; mọi lệnh ghi đi qua src/ledger.
 */
import { create } from 'zustand';
import { onSnapshot } from 'firebase/firestore';
import { userCollection } from '../lib/firebase';

// Biên Firestore schema-less: dữ liệu thật được kiểm bằng hợp đồng types/db.ts (test trên bản sao lưu)
// (any: các hàm finance-core khai báo kiểu lỏng, TS từ chối interface có index signature vì "weak type")
export type Doc = any;

/**
 * Bản ghi cũ thiếu `year`/`month` (vd. 3 bản ghi 23/01/2026) sẽ vô hình trong mọi báo cáo theo kỳ.
 * Suy từ ngày giao dịch NGAY KHI NẠP (chỉ trong bộ nhớ — không ghi lại Firestore).
 */
export const withPeriod = (doc: Doc): Doc => {
  if (doc.year != null && doc.month != null) return doc;
  const t = doc.transactionDate;
  const d: Date | null = t?.toDate ? t.toDate() : t ? new Date(t) : null;
  if (!d || Number.isNaN(d.getTime())) return doc;
  return { ...doc, year: doc.year ?? d.getFullYear(), month: doc.month ?? d.getMonth() + 1 };
};
const PERIODIC = new Set<string>(['incomes', 'expenses']);

export const COLLECTIONS = [
  'sources', 'incomes', 'expenses', 'debts', 'creditPayments', 'transfers',
  'plannedExpenses', 'plannedIncomes', 'plannedPayments', 'budgets', 'goals',
  'expenseCategories', 'incomeCategories', 'budgetEnvelopes', 'recurringTemplates'
] as const;
export type CollectionName = typeof COLLECTIONS[number];

/** Cần đủ các collection này để tính tài sản ròng & sức khoẻ — dùng để hiện khung chờ. */
export const CORE: CollectionName[] = ['sources', 'incomes', 'expenses', 'debts'];

interface DataState {
  data: Record<CollectionName, Doc[]>;
  ready: Record<CollectionName, boolean>;
  /** Đã nhận dữ liệu từ MÁY CHỦ (không chỉ từ bộ nhớ đệm trên máy) */
  synced: Record<CollectionName, boolean>;
  errors: Partial<Record<CollectionName, string>>;
  start: (uid: string) => () => void;
}

const empty = <T,>(v: T) => Object.fromEntries(COLLECTIONS.map(c => [c, v])) as Record<CollectionName, T>;

export const useData = create<DataState>((set) => ({
  data: empty<Doc[]>([]),
  ready: empty(false),
  synced: empty(false),
  errors: {},
  start: (uid) => {
    set({ data: empty<Doc[]>([]), ready: empty(false), synced: empty(false), errors: {} });
    const unsubs = COLLECTIONS.map(name =>
      onSnapshot(
        userCollection(uid, name),
        // Có bộ nhớ đệm: lần đầu có thể trả dữ liệu cũ trên máy (fromCache) rồi mới tới dữ liệu máy chủ — theo dõi để biết lúc nào đã đồng bộ
        { includeMetadataChanges: true },
        (snap) => set(s => {
          const synced = !snap.metadata.fromCache;
          const first = !s.ready[name];
          // Chỉ đổi metadata (fromCache → máy chủ) mà dữ liệu không đổi: không dựng lại mảng, tránh vẽ lại cả ứng dụng
          if (!first && snap.docChanges().length === 0) return synced && !s.synced[name] ? { synced: { ...s.synced, [name]: true } } : s;
          return {
            data: { ...s.data, [name]: snap.docs.map(d => PERIODIC.has(name) ? withPeriod({ id: d.id, ...d.data() }) : ({ id: d.id, ...d.data() })) },
            ready: { ...s.ready, [name]: true },
            synced: { ...s.synced, [name]: s.synced[name] || synced }
          };
        }),
        (err) => set(s => ({
          errors: { ...s.errors, [name]: err.message },
          ready: { ...s.ready, [name]: true }
        }))
      )
    );
    return () => unsubs.forEach(u => u());
  }
}));

export const useCoreReady = (): boolean => useData(s => CORE.every(c => s.ready[c]));
/** Dữ liệu lõi đã khớp máy chủ (dùng trước khi GHI ảnh chụp dựa trên số liệu đang hiển thị). */
export const useCoreSynced = (): boolean => useData(s => CORE.every(c => s.synced[c]));
